/* eslint-disable react-hooks/exhaustive-deps */
import React,{FC, useEffect, useMemo, useState} from "react";
import { Dropdown, FormControl } from "react-bootstrap";

type DropdownProps<T> = {
  econ?:string;
  /** Items to show in the dropdown */
  items: T[];
  /** Required: how to display each item */
  getLabel: (item: T) => string;
  /** Optional: stable key (defaults to index) */
  getKey?: (item: T, index: number) => string;

  /** Placeholder text when nothing selected */
  placeholder?: string;
  /** Controlled selected item (by item reference or key) */
  value?: T | null;
  /** If using key-based value, provide a resolver to find the item by key */
  findByValueKey?: (value: unknown, items: T[]) => T | undefined;

  /** Called when an item is selected */
  onSelect: (item: T) => void;
  searchable?: boolean;
  /** UI/behavior */
  disabled?: boolean;
  className?: string;
  menuClassName?: string;
  autoClearSearchOnClose?: boolean;
  /** Optional: custom filter (defaults to label includes) */
  filterFn?: (item: T, search: string) => boolean;
}

function defaultFilter<T>(getLabel: (i: T) => string) {
  return (item: T, search: string) =>
    getLabel(item).toLowerCase().includes(search.toLowerCase());
}


const CustomDropdown = <T,>({
  items,
  getLabel,
  getKey,
  placeholder = "Select…",
  value = null,
  findByValueKey,
  onSelect,
  disabled = false,
  searchable = false,
  className = "",
  menuClassName = "",
  autoClearSearchOnClose = true,
  filterFn,
}: DropdownProps<T>)  => {
  const [searchTerm, setSearchTerm] = useState<string>(''); 
  const [selectedItem, setSelectedItem] = useState<T | null>(null); 

  useEffect(()=>{
    if (value == null) {
      setSelectedItem(null);
      return;
    }
    setSelectedItem(value);
  },[value, items])


  const filtered = useMemo(() => {
    const f = filterFn ?? defaultFilter(getLabel);
    return searchTerm ? items.filter((it) => f(it, searchTerm)) : items;
  }, [items, searchTerm, filterFn, getLabel]);

  const handlePick = (item: T) => {
    setSelectedItem(item);
    onSelect(item);
  };

  const clearSearch = () => {
    if (autoClearSearchOnClose) setSearchTerm("");
  };

  return ( 
    <Dropdown
      className={`${className ?? ""}`}
      onToggle={(show) => {
        if (!show) clearSearch();
      }}
    >
      <Dropdown.Toggle
        id="generic-search-dropdown"
        className="btn btn-sm fw-normal text-start w-100 d-flex justify-content-between align-items-center"
        variant="label-dropdown"
        disabled={disabled}
      >
        <div
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "clip",
            maxWidth: "220px",
          }}
        >
          {selectedItem ? (
            <>
              {getLabel(selectedItem)}
            </>
          ) : (
            placeholder
          )}
        </div>
      </Dropdown.Toggle>

      <Dropdown.Menu className={`w-100 ${menuClassName ?? ""}`}>
        {searchable && (
          <FormControl
            type="text"
            size="sm"
            placeholder="Search…"
            className="mb-2"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        )}
        {filtered.map((item, idx) => {
          const key = getKey ? getKey(item, idx) : String(idx);
          return (
            <Dropdown.Item
              key={key}
              role="menuitem"
              onClick={() => handlePick(item)}
              title={getLabel(item)}
            >
              {getLabel(item)}
            </Dropdown.Item>
          );
        })}
        {filtered.length === 0 && (
          <div className="px-3 py-1 text-muted small">No results</div>
        )}
      </Dropdown.Menu>
    </Dropdown>
  )
}

export default CustomDropdown